<script setup>
import { computed, h, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../stores/auth';
import { usePolicyLinesStore } from '../../stores/policyLines';
import { canAssignBrokers } from '../../helpers/roles';
import { money } from '../../helpers/coverage';
import { TERM_TIPS } from '../../helpers/termTips';
import { analyzeImport, commitImport, reviewImport } from '../../api/policyholders';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlDropdown from '../xl/XlDropdown.vue';
import XlKpi from '../xl/XlKpi.vue';
import XlStatusIcon from '../xl/XlStatusIcon.vue';
import XlSwitch from '../xl/XlSwitch.vue';
import XlTable from '../xl/XlTable.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';

const COLUMNS = [
  { key: 'first_name', label: 'First name', required: true },
  { key: 'last_name', label: 'Last name', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'phone', label: 'Phone' },
  { key: 'policy_line', label: 'Policy line' },
  { key: 'policy_number', label: 'Policy number' },
  { key: 'monthly_premium', label: 'Monthly premium' },
  { key: 'renewal_date', label: 'Renewal date' },
  { key: 'broker', label: 'Broker' },
];

const STEPS = ['Upload', 'Match columns', 'Review', 'Import'];

const SKIPPED = {
  invalid_email: "Email isn't valid",
  missing_email: 'Email is missing',
  missing_name: 'A name is missing',
  duplicate: 'The same policy twice',
  works_here: 'They work in this hub',
};

const ICONS = {
  back: ['m14.2 18-6-6 6-6'],
  chev: ['m9.8 18 6-6-6-6'],
  check: ['m5.8 12.3 4.2 4.2L18.2 7.8'],
  plus: ['M12 6.2v11.6M6.2 12h11.6'],
  dl: ['M12 4.2v10.4M8 10.8l4 4 4-4', 'M4.5 19.6h15'],
  file: ['M13.6 4H7.5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V9z', 'M13.6 4v5h4.9'],
  alert: ['M12 4.4 3.6 19.6h16.8z', 'M12 10v3.8M12 16.8h.01'],
};

// The prototype's icon set: thinner strokes read heavier at small sizes, so
// the stroke thickens as the glyph shrinks.
const Icon = ({ name, size = 16 }) => h('svg', {
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': size < 13 ? 2.1 : size < 17 ? 1.9 : 1.7,
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
  'aria-hidden': 'true',
}, ICONS[name].map((d) => h('path', { d })));
Icon.props = ['name', 'size'];

// A term the portal explains carries its tip, underlined unless it sits in a tag.
const Term = ({ label, plain }, { slots }) => {
  const text = slots.default?.() ?? label;

  return TERM_TIPS[label] ? h(XlTooltip, { text: TERM_TIPS[label], placement: 'bottom', underline: !plain }, () => text) : text;
};
Term.props = { label: String, plain: Boolean };

const router = useRouter();
const authStore = useAuthStore();
const policyLines = usePolicyLinesStore();

const step = ref(1);
const busy = ref(false);
const error = ref('');
const dragging = ref(false);

const fileName = ref('');
const sheet = ref(null);
const mapping = ref({});
const review = ref(null);
const updateExisting = ref(true);
const sendInvitations = ref(true);
const result = ref(null);

const hubLines = computed(() => review.value?.lines ?? policyLines.options.map((line) => line.name));

// The example uses this hub's own lines, so whoever copies it copies names that match.
const example = computed(() => {
  const [first = 'Home', second = first] = hubLines.value;

  return [
    ['Jane', 'Doe', 'jane.doe@example.com', '555-555-5551', first, 'HO-4821-CHI', '142.00', '2027-03-15', 'Sam Rivera'],
    ['Jane', 'Doe', 'jane.doe@example.com', '555-555-5551', second, 'AU-9033-CHI', '88.50', '2026-11-02', 'Sam Rivera'],
    ['Marcus', 'Lee', 'marcus.lee@example.com', '555-555-5552', second, 'AU-6120-CHI', '74.25', '2027-01-20', 'Sam Rivera'],
  ];
});

const headingOptions = computed(() => [
  ...(sheet.value?.headings ?? []),
  { value: '', label: '— not in the file' },
]);

const canCheck = computed(() => COLUMNS.every((column) => !column.required || mapping.value[column.key]));

const rows = computed(() => review.value?.rows ?? []);
const importable = computed(() => rows.value.filter((row) => row.verdict === 'import'));
const counts = computed(() => review.value?.counts ?? { rows: 0, skipped: 0, new: 0, existing: 0 });
const comingIn = computed(() => counts.value.new + (updateExisting.value ? counts.value.existing : 0));

const rowNumber = (row) => row.index + 1;
const plural = (n, one, many) => (n === 1 ? one : many);
const rowsLabel = (numbers) => `${plural(numbers.length, 'Row', 'Rows')} ${numbers.join(', ')}`;
const quoted = (names) => names.map((name) => `“${name}”`).join(' and ');

const skippedGroups = computed(() => Object.entries(SKIPPED)
  .map(([reason, label]) => ({
    reason,
    label,
    numbers: rows.value
      .filter((row) => row.reason === reason)
      .map((row) => (row.duplicate_of ? `${rowNumber(row)} (same as row ${row.duplicate_of})` : rowNumber(row))),
  }))
  .filter((group) => group.numbers.length));

const unknownLineRows = computed(() => importable.value.filter((row) => row.unknown_lines.length));
const lineKey = (name) => name.trim().toLowerCase().replace(/\s+/g, ' ');
const sameLine = (a, b) => lineKey(a) === lineKey(b);
const unknownLineNames = computed(() => unknownLineRows.value.flatMap((row) => row.unknown_lines)
  .filter((name, index, all) => all.findIndex((other) => sameLine(other, name)) === index));

// A person, not a row: their other rows may still come in.
const withNoPolicy = computed(() => {
  const people = new Map();

  importable.value.forEach((row) => {
    const person = people.get(row.data.email) ?? { name: `${row.data.first_name} ${row.data.last_name}`, refusedAll: true };
    person.refusedAll &&= row.refused;
    people.set(row.data.email, person);
  });

  return [...people.values()].filter((person) => person.refusedAll).map((person) => person.name);
});

const unknownBrokerRows = computed(() => importable.value.filter((row) => row.broker_unknown));
const unknownBrokerNames = computed(() => [...new Set(unknownBrokerRows.value.map((row) => row.data.broker))]);

const hasNotes = computed(() => skippedGroups.value.length || unknownLineRows.value.length || unknownBrokerRows.value.length);

const outcome = (row) => {
  if (row.verdict === 'skipped') return { label: 'Skipped', tone: row.reason === 'duplicate' ? 'warning' : 'error' };
  if (row.refused) return { label: "Won't come in", tone: 'error' };
  if (row.person === 'new') return { label: 'Added', tone: 'success' };

  return { label: updateExisting.value ? 'Updated' : 'Left alone', tone: 'brand' };
};

const isUnknownLine = (row, line) => row.unknown_lines.includes(line);

// Left alone is skipped from where the reader stands: nothing of theirs changed.
const notImported = computed(() => (result.value ? result.value.skipped + result.value.left_alone : 0));
const retryable = computed(() => !!result.value?.failed.some((failure) => failure.retry !== false));

const fail = (e, fallback) => {
  error.value = e.response?.data?.message || fallback;
};

const pick = async (file) => {
  dragging.value = false;
  if (!file) return;

  busy.value = true;
  error.value = '';

  try {
    sheet.value = await analyzeImport(file);
    fileName.value = file.name;
    mapping.value = { ...sheet.value.mapping };
    step.value = 2;
  } catch (e) {
    fail(e, 'That file could not be read.');
  } finally {
    busy.value = false;
  }
};

const restart = () => {
  step.value = 1;
  sheet.value = null;
  review.value = null;
  error.value = '';
};

const check = async () => {
  busy.value = true;
  error.value = '';

  try {
    review.value = await reviewImport(sheet.value.rows, mapping.value);
    step.value = 3;
  } catch (e) {
    fail(e, 'Those rows could not be checked.');
  } finally {
    busy.value = false;
  }
};

const runImport = async () => {
  busy.value = true;
  error.value = '';

  try {
    result.value = await commitImport(sheet.value.rows, mapping.value, {
      update_existing: updateExisting.value,
      send_invitations: sendInvitations.value,
    });
    step.value = 4;
  } catch (e) {
    fail(e, 'The import could not be completed.');
  } finally {
    busy.value = false;
  }
};

const csvCell = (cell) => {
  const text = String(cell ?? '');

  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const download = (name, lines) => {
  const csv = lines.map((cells) => cells.map(csvCell).join(',')).join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([`${csv}\n`], { type: 'text/csv' }));
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
};

const downloadTemplate = () => download('policyholders-template.csv', [COLUMNS.map((column) => column.label)]);

const happened = (row) => {
  const failure = result.value.failed.find((f) => f.row === rowNumber(row));
  if (failure) return `Didn't finish — ${failure.message}`;
  if (row.verdict === 'skipped') return `Skipped — ${SKIPPED[row.reason]}${row.duplicate_of ? ` (same as row ${row.duplicate_of})` : ''}`;

  return outcome(row).label;
};

const downloadReport = () => download('import-report.csv', [
  ['Row', 'Name', 'Email', 'Policy line', 'Policy number', 'What happened', 'Not a policy line here'],
  ...rows.value.map((row) => [
    rowNumber(row),
    [row.data.first_name, row.data.last_name].filter(Boolean).join(' '),
    row.data.email,
    row.data.policy_lines.join(', '),
    row.data.policy_number,
    happened(row),
    row.unknown_lines.join(', '),
  ]),
]);

const backToList = () => router.push('/policyholders');

onMounted(() => {
  if (!canAssignBrokers(authStore.userData)) {
    router.replace('/policyholders');
    return;
  }

  policyLines.load();
});
</script>

<template>
  <div class="ph-import" data-xl-gen="northstar" data-testid="policyholder-import">
    <XlButton variant="ghost" size="sm" class="ph-import__back" @click="backToList">
      <template #icon><Icon name="back" :size="15" /></template>
      Back to policyholders
    </XlButton>

    <h1>Import policyholders</h1>
    <p class="ph-import__lede">Bring a spreadsheet in. Nothing is saved until the last step.</p>

    <ol class="ph-import__steps" data-testid="import-steps">
      <template v-for="(label, i) in STEPS" :key="label">
        <li class="ph-import__step" :class="{ 'is-now': step === i + 1, 'is-done': step > i + 1 }">
          <i><Icon v-if="step > i + 1" name="check" :size="13" /><template v-else>{{ i + 1 }}</template></i>
          <span>{{ label }}</span>
        </li>
        <li v-if="i < STEPS.length - 1" class="ph-import__step-line" :class="{ 'is-done': step > i + 1 }" aria-hidden="true" />
      </template>
    </ol>

    <p v-if="error" class="ph-import__error" role="alert" data-testid="import-error">{{ error }}</p>

    <!-- 1. Upload -->
    <template v-if="step === 1">
      <div class="ph-import__grid">
        <XlCard padded>
          <label
            class="ph-import__drop"
            :class="{ 'is-over': dragging, 'is-busy': busy }"
            data-testid="import-drop"
            @dragover.prevent="dragging = true"
            @dragleave="dragging = false"
            @drop.prevent="pick($event.dataTransfer.files[0])"
          >
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              class="ph-import__file"
              data-testid="import-file"
              :disabled="busy"
              @change="pick($event.target.files[0])"
            />
            <XlStatusIcon :size="52" :glyph="29" class="ph-import__drop-icon"><Icon name="dl" :size="29" /></XlStatusIcon>
            <b>{{ busy ? 'Reading the file…' : 'Choose a file, or drop one here' }}</b>
            <span class="ph-import__drop-hint">CSV or Excel · up to 2,000 rows</span>
          </label>
          <p class="ph-import__fine">Nothing is imported yet — you'll see every row before anything is saved.</p>
        </XlCard>

        <XlCard padded>
          <b class="ph-import__title">What the file needs</b>
          <p class="ph-import__sub">Column names don't have to match exactly — you'll line them up on the next step.</p>
          <div v-for="column in COLUMNS" :key="column.key" class="ph-import__need">
            <XlStatusIcon v-if="column.required" :glyph="16"><Icon name="check" /></XlStatusIcon>
            <span v-else class="ph-import__plain-icon"><Icon name="plus" /></span>
            <div class="ph-import__need-text">
              <b><Term :label="column.label" /></b>
              <span>{{ column.required ? 'Required' : 'Optional, but useful' }}</span>
            </div>
          </div>
          <XlButton variant="ghost" class="ph-import__block" data-testid="download-template" @click="downloadTemplate">
            <template #icon><Icon name="file" :size="15" /></template>
            Download a blank template
          </XlButton>
          <div class="ph-import__tip">
            <Icon name="alert" :size="14" />
            <span>Include <b><Term label="Policy line">policy line</Term></b> and <b><Term label="Renewal date">renewal date</Term></b> if you have them. Without those two,
              an imported policyholder sits in no segment and gets no renewal reminders.</span>
          </div>
        </XlCard>
      </div>

      <XlCard padded class="ph-import__template" data-testid="import-template">
        <div class="ph-import__template-head">
          <div>
            <b class="ph-import__title">What the template looks like</b>
            <p class="ph-import__sub">
              One row per policy. Somebody with three policies is three rows sharing an email —
              that's how the policy number, the premium and the renewal date stay attached to the
              right policy.</p>
          </div>
          <XlButton variant="ghost" size="sm" @click="downloadTemplate">
            <template #icon><Icon name="dl" :size="14" /></template>
            Download it
          </XlButton>
        </div>
        <XlTable class="ph-import__example">
          <template #head>
            <th v-for="column in COLUMNS" :key="column.key"><Term :label="column.label" /></th>
          </template>
          <tr v-for="(values, r) in example" :key="r">
            <td v-for="(value, c) in values" :key="c" :class="{ 'is-figure': c === 5 || c === 6 }">{{ value }}</td>
          </tr>
        </XlTable>
        <p class="ph-import__fine" data-testid="import-lines-note">
          <template v-if="hubLines.length">
            <b>Policy line</b> has to match a line this hub already has — {{ hubLines.join(', ') }}.
            Anything else is shown to you and skipped; an import never creates a policy line.
          </template>
          <template v-else>
            This hub has no policy lines yet, so no policies can come in. Add them under Policy lines first.
          </template>
          <b>Monthly premium</b> is a number: 142.00, not $142/mo.
        </p>
      </XlCard>
    </template>

    <!-- 2. Match columns -->
    <template v-if="step === 2">
      <XlCard padded class="ph-import__file-card">
        <XlStatusIcon tone="success" :glyph="23"><Icon name="check" :size="23" /></XlStatusIcon>
        <div class="ph-import__grow">
          <b class="ph-import__title">{{ fileName }}</b>
          <div class="ph-import__muted">
            {{ sheet.rows.length }} {{ plural(sheet.rows.length, 'row', 'rows') }} ·
            {{ sheet.headings.length }} {{ plural(sheet.headings.length, 'column', 'columns') }} read
          </div>
          <div v-if="sheet.truncated" class="ph-import__muted">Only the first {{ sheet.row_limit }} rows will be imported.</div>
        </div>
        <XlButton variant="ghost" size="sm" @click="restart">Choose a different file</XlButton>
      </XlCard>

      <XlCard padded data-testid="import-mapping">
        <b class="ph-import__title">Line up the columns</b>
        <p class="ph-import__sub ph-import__sub--map">We've guessed from the headings. Change anything that looks wrong.</p>
        <div v-for="column in COLUMNS" :key="column.key" class="ph-import__map-row" :data-testid="`map-${column.key}`">
          <XlTag tone="brand" class="ph-import__map-field"><Term :label="column.label" plain /></XlTag>
          <span class="ph-import__arrow" aria-hidden="true">←</span>
          <XlDropdown
            class="ph-import__map-pick"
            :options="headingOptions"
            :model-value="mapping[column.key] ?? ''"
            :aria-label="`Column for ${column.label}`"
            placeholder="— not in the file"
            @update:model-value="(value) => (mapping[column.key] = value || null)"
          />
          <XlTag v-if="mapping[column.key]" tone="success"><Icon name="check" :size="11" /> matched</XlTag>
          <XlTag v-else-if="column.required" tone="error">needed</XlTag>
        </div>
      </XlCard>

      <div class="ph-import__actions">
        <XlButton variant="ghost" :disabled="busy" @click="restart">Back</XlButton>
        <XlButton :disabled="!canCheck" :loading="busy" data-testid="import-check" @click="check">
          Check the rows <Icon name="chev" :size="15" />
        </XlButton>
      </div>
    </template>

    <!-- 3. Review: read-only. A file is fixed in the thing that made it. -->
    <template v-if="step === 3">
      <XlCard padded class="ph-import__answer" data-testid="import-answer">
        <XlStatusIcon tone="success" :glyph="25"><Icon name="check" :size="25" /></XlStatusIcon>
        <div class="ph-import__grow">
          <b class="ph-import__headline">{{ comingIn }} {{ plural(comingIn, 'person comes in', 'people come in') }}</b>
          <div class="ph-import__muted">
            {{ counts.rows }} {{ plural(counts.rows, 'row', 'rows') }} in {{ fileName }}<template v-if="counts.skipped"> · {{ counts.skipped }} won't be imported</template>
          </div>
        </div>
      </XlCard>

      <XlCard v-if="hasNotes" padded tone="warning" class="ph-import__notes" data-testid="import-notes">
        <XlStatusIcon tone="warning" :glyph="23"><Icon name="alert" :size="23" /></XlStatusIcon>
        <div class="ph-import__grow">
          <b class="ph-import__title">What won't come in</b>
          <p class="ph-import__notes-lede">
            Nothing here stops the import. Everything else is added as normal —
            fix these in the file and import again, or add them by hand afterwards.</p>

          <div class="ph-import__note-list">
            <div v-for="group in skippedGroups" :key="group.reason" class="ph-import__note" :data-testid="`skipped-${group.reason}`">
              <b>{{ group.numbers.length }} {{ plural(group.numbers.length, 'row', 'rows') }} skipped — {{ group.label.toLowerCase() }}</b>
              <span>{{ rowsLabel(group.numbers) }}</span>
            </div>

            <div v-if="unknownLineRows.length" class="ph-import__note" data-testid="unknown-lines">
              <b>{{ quoted(unknownLineNames) }} {{ plural(unknownLineNames.length, "isn't a policy line", "aren't policy lines") }} here</b>
              <span>{{ rowsLabel(unknownLineRows.map(rowNumber)) }} — a row with none of your lines won't come in; one that also names a line of yours comes in on that line.
                Your lines: {{ hubLines.join(', ') || 'none yet' }}.</span> <span v-if="withNoPolicy.length" data-testid="no-policy">{{ withNoPolicy.join(' and ') }} {{ plural(withNoPolicy.length, "has no other line, so won't come in", "have no other line, so won't come in") }}.</span>
            </div>

            <div v-if="unknownBrokerRows.length" class="ph-import__note" data-testid="unknown-brokers">
              <b>{{ quoted(unknownBrokerNames) }} {{ plural(unknownBrokerNames.length, "isn't a broker", "aren't brokers") }} here</b>
              <span>{{ rowsLabel(unknownBrokerRows.map(rowNumber)) }} — those people come in unassigned.</span>
            </div>
          </div>
        </div>
      </XlCard>

      <XlCard class="ph-import__rows">
        <XlTable data-testid="import-rows">
          <template #head>
            <th class="ph-import__col-row">Row</th>
            <th>Name</th>
            <th>Email</th>
            <th><Term label="Policy line" /></th>
            <th>Policy number</th>
            <th class="is-right">Premium</th>
            <th><Term label="Renewal" /></th>
            <th><Term label="Broker" /></th>
            <th>What happens</th>
          </template>
          <tr v-for="row in rows" :key="row.index" :class="{ 'is-skipped': row.verdict === 'skipped' }" :data-testid="`import-row-${row.index}`">
            <td class="ph-import__row-number">{{ rowNumber(row) }}</td>
            <td class="ph-import__name">
              <b>{{ [row.data.first_name, row.data.last_name].filter(Boolean).join(' ') }}</b>
              <XlTag v-if="!row.data.first_name" tone="error">no first name</XlTag>
              <XlTag v-if="!row.data.last_name" tone="error">no last name</XlTag>
            </td>
            <td class="ph-import__email">
              <template v-if="row.data.email">{{ row.data.email }}</template>
              <XlTag v-else tone="error">no email</XlTag>
            </td>
            <td>
              <span v-if="row.data.policy_lines.length" class="ph-import__lines">
                <XlTag
                  v-for="line in row.data.policy_lines"
                  :key="line"
                  :tone="isUnknownLine(row, line) ? 'warning' : 'brand'"
                  :class="{ 'is-struck': isUnknownLine(row, line) }"
                >{{ line }}</XlTag>
              </span>
              <XlTag v-else tone="error">none</XlTag>
            </td>
            <td class="ph-import__muted-cell is-figure">{{ row.data.policy_number || '—' }}</td>
            <td class="is-right is-figure">
              <template v-if="row.data.monthly_premium !== null">{{ money(row.data.monthly_premium) }}</template>
              <XlTag v-else-if="row.data.monthly_premium_raw" tone="warning" class="is-struck" title="Not a number, so this policy comes in without a premium">{{ row.data.monthly_premium_raw }}</XlTag>
              <template v-else>—</template>
            </td>
            <td class="ph-import__muted-cell">
              <template v-if="row.data.renewal_date">{{ row.data.renewal_date }}</template>
              <XlTag v-else-if="row.data.renewal_date_raw" tone="warning" class="is-struck" title="Not a date we can read, so this policy comes in without one">{{ row.data.renewal_date_raw }}</XlTag>
              <template v-else>—</template>
            </td>
            <td class="ph-import__muted-cell">
              <XlTag v-if="row.broker_unknown" tone="warning" class="is-struck">{{ row.data.broker }}</XlTag>
              <template v-else>{{ row.data.broker || '—' }}</template>
            </td>
            <td><XlTag :tone="outcome(row).tone" data-testid="row-outcome"><Term :label="outcome(row).label" plain /></XlTag></td>
          </tr>
        </XlTable>
      </XlCard>

      <XlCard padded class="ph-import__toggles">
        <XlSwitch
          v-if="counts.existing"
          v-model="updateExisting"
          :label="`Update the ${counts.existing} already in your portal`"
          :hint="updateExisting ? 'Their policies, numbers, premiums, dates and broker are taken from the file' : 'They\'re left exactly as they are'"
          data-testid="toggle-update"
        />
        <XlSwitch
          v-model="sendInvitations"
          label="Email the new people an invitation"
          :hint="sendInvitations ? `${counts.new} ${plural(counts.new, 'person gets', 'people get')} a link to set a password` : 'Nobody is emailed — you can invite them later'"
          data-testid="toggle-invite"
        />
      </XlCard>

      <div class="ph-import__actions">
        <XlButton variant="ghost" :disabled="busy" @click="step = 2">Back</XlButton>
        <XlButton :disabled="!comingIn" :loading="busy" data-testid="import-commit" @click="runImport">
          Import {{ comingIn }} {{ plural(comingIn, 'person', 'people') }} <Icon name="chev" :size="15" />
        </XlButton>
      </div>
    </template>

    <!-- 4. Done -->
    <XlCard v-if="step === 4" padded class="ph-import__done" data-testid="import-done">
      <XlStatusIcon tone="success" :size="56" :glyph="31" class="ph-import__done-icon"><Icon name="check" :size="31" /></XlStatusIcon>
      <h2>Import finished</h2>
      <p class="ph-import__done-line">
        {{ result.added }} added<template v-if="result.updated"> · {{ result.updated }} updated</template><template v-if="notImported"> · {{ notImported }} skipped</template>
      </p>
      <div class="ph-import__kpis">
        <XlKpi label="Added" :value="result.added" tone="success" />
        <XlKpi label="Updated" :value="result.updated" tone="brand" />
        <XlKpi label="Skipped" :value="notImported" :tone="notImported ? 'warning' : ''" :tooltip="TERM_TIPS.Skipped" />
      </div>
      <p v-if="result.invited" class="ph-import__done-note">
        {{ result.invited }} invitation {{ plural(result.invited, 'email is', 'emails are') }} on their way. They'll show under
        <b>Not registered</b> until each person finishes registering.</p>
      <div v-if="result.failed.length" class="ph-import__failed" data-testid="import-failed">
        <b>{{ result.failed.length }} {{ plural(result.failed.length, "row didn't", "rows didn't") }} finish</b>
        <span v-for="failure in result.failed" :key="`${failure.row}-${failure.email}`">Row {{ failure.row }} · {{ failure.email }} — {{ failure.message }}</span>
        <span v-if="retryable">Import the same file again to finish them — people already in are updated, never added twice.</span>
      </div>
      <div class="ph-import__done-actions">
        <XlButton variant="ghost" data-testid="download-report" @click="downloadReport">Download the report</XlButton>
        <XlButton @click="backToList">See your policyholders</XlButton>
      </div>
    </XlCard>
  </div>
</template>

<style scoped>
.ph-import {
  padding: var(--setup-page-padding) var(--setup-page-padding) 60px;
  font-family: var(--xl-font-family);
  color: var(--xl-color-primary-text);
}

.ph-import h1 { margin: 0 0 6px; font-size: 30px; font-weight: var(--xl-font-weight-bold); letter-spacing: -0.03em; }
.ph-import__back { margin-bottom: 18px; }
.ph-import__lede { margin: 0 0 22px; font-size: 15px; color: var(--xl-color-outline); }

.ph-import__steps { display: flex; align-items: center; flex-wrap: wrap; margin: 0 0 24px; padding: 0; list-style: none; }
.ph-import__step {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--xl-color-outline-variant);
  white-space: nowrap;
}
.ph-import__steps i {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  border: 1.5px solid var(--xl-color-border);
  border-radius: 50%;
  background: #fff;
  font-style: normal;
  font-size: 12.5px;
  font-weight: 700;
}
.ph-import__steps .is-now { color: var(--xl-color-primary); }
.ph-import__steps .is-now i { border-color: var(--xl-color-primary); background: var(--xl-color-primary); color: #fff; }
.ph-import__steps .is-done { color: var(--xl-color-success); }
.ph-import__steps .is-done i { border-color: var(--xl-color-success); background: var(--xl-color-success); color: #fff; }
.ph-import__step-line { flex: 1; min-width: 22px; height: 2px; margin: 0 12px; border-radius: 2px; background: var(--xl-color-border); }
.ph-import__step-line.is-done { background: var(--xl-color-success); }

.ph-import__error {
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--xl-color-error-container);
  color: var(--xl-color-error);
  font-size: 13.5px;
}

.ph-import__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: start; }
.ph-import__grid > .xl-card { margin-bottom: 0; }

.ph-import__drop {
  position: relative;
  display: block;
  padding: 38px 22px;
  border: 2px dashed #D9C6E8;
  border-radius: 18px;
  background: var(--xl-color-primary-container);
  text-align: center;
  cursor: pointer;
  transition: 0.14s;
}
.ph-import__drop:hover, .ph-import__drop.is-over { border-color: var(--xl-color-primary); background: #F1E8F7; }
.ph-import__drop.is-busy { cursor: progress; }
.ph-import__drop b { display: block; font-size: 16px; }
.ph-import__drop-hint { display: block; margin-top: 6px; font-size: 13.5px; color: var(--xl-color-outline); }
.ph-import__drop-icon { margin: 0 auto 12px; }
.ph-import__file { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }

.ph-import__title { font-size: 15px; }
.ph-import__sub { margin: 6px 0 14px; font-size: 13.5px; color: var(--xl-color-outline); }
.ph-import__sub--map { margin-bottom: 16px; }
.ph-import__fine { margin: 14px 0 0; font-size: 12.5px; color: var(--xl-color-outline); }
.ph-import__muted { font-size: 13.5px; color: var(--xl-color-outline); }
.ph-import__grow { flex: 1; min-width: 200px; }

.ph-import__need { display: flex; align-items: center; gap: 14px; padding: 9px 0; border-bottom: 1px solid var(--xl-color-border-subtle); }
.ph-import__need-text { flex: 1; min-width: 0; }
.ph-import__need-text b { display: block; font-size: 14px; }
.ph-import__need-text > span { font-size: 13px; color: var(--xl-color-outline); }
.ph-import__need .xl-status-icon { height: 30px; }
.ph-import__plain-icon { display: grid; place-items: center; width: 42px; height: 30px; flex: 0 0 42px; border-radius: 13px; }
.ph-import__answer .xl-status-icon { height: 46px; }

.ph-import__block { width: 100%; margin-top: 14px; }

.ph-import__tip {
  margin-top: 14px;
  padding: 13px 15px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: var(--xl-color-surface);
  font-size: 13px;
  color: var(--xl-color-secondary-text);
}
.ph-import__tip svg { display: block; }

.ph-import__template { margin-top: 20px; }
.ph-import__template-head { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.ph-import__template-head > div { flex: 1; min-width: 240px; }
.ph-import__template-head .ph-import__sub { margin: 5px 0 0; font-size: 13px; }
.ph-import__example :deep(table) { min-width: 820px; font-size: 13px; }
.ph-import__example :deep(td) { white-space: nowrap; color: var(--xl-color-secondary-text); }

.is-figure { font-variant-numeric: tabular-nums; }
.is-right { text-align: right !important; }
.is-struck { text-decoration: line-through; }

.ph-import__file-card, .ph-import__answer, .ph-import__notes { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.ph-import__answer { gap: 14px; }
.ph-import__notes { align-items: flex-start; }
.ph-import__headline { font-size: 17px; }
.ph-import__answer .ph-import__muted { margin-top: 2px; }

.ph-import__map-row { display: flex; align-items: center; gap: 12px; padding: 7px 0; flex-wrap: wrap; }
.ph-import__map-field { width: 132px; height: 34px; padding: 0 12px; justify-content: flex-start; }
.ph-import__arrow { color: var(--xl-color-outline-variant); }
.ph-import__map-pick { flex: 1; min-width: 180px; }
.ph-import__map-pick :deep(.xl-dropdown__control) { border-radius: 11px; }

.ph-import__actions { display: flex; gap: 10px; justify-content: flex-end; align-items: center; flex-wrap: wrap; margin-top: 16px; }

.ph-import__notes-lede { margin: 5px 0 0; font-size: 13.5px; line-height: 1.6; color: var(--xl-color-secondary-text); }
.ph-import__note-list { margin-top: 14px; }
.ph-import__note { padding: 10px 0; border-bottom: 1px solid #F2DCB9; }
.ph-import__note:last-child { border-bottom: 0; }
.ph-import__note b { display: block; font-size: 14px; }
.ph-import__note span { font-size: 13px; color: var(--xl-color-outline); }

.ph-import :deep(.xl-table th) { height: 46px; }

.ph-import__rows { margin-top: 16px; }
.ph-import__col-row { width: 44px; }
.ph-import__rows .ph-import__row-number { color: var(--xl-color-outline-variant); font-variant-numeric: tabular-nums; }
.ph-import__rows .ph-import__email { color: var(--xl-color-outline); }
.ph-import__rows .ph-import__muted-cell { color: var(--xl-color-outline); white-space: nowrap; }
.ph-import__lines { display: inline-flex; gap: 4px; flex-wrap: wrap; }
.ph-import__name .xl-tag { margin-left: 4px; }
.ph-import__rows :deep(tr.is-skipped) { opacity: 0.55; }
.ph-import__rows :deep(td) { font-size: 14px; }
.ph-import__rows :deep(th) { white-space: normal; }

.ph-import__toggles { margin-top: 16px; }
.ph-import__toggles :deep(.xl-switch) { display: flex; width: 100%; padding: 12px 0; border-bottom: 1px solid var(--xl-color-border-subtle); }
.ph-import__toggles :deep(.xl-switch:last-child) { border-bottom: 0; }
.ph-import__toggles :deep(.xl-switch__text) { display: block; flex: 1; }
.ph-import__toggles :deep(.xl-switch__text b) { display: block; font-size: 15px; font-weight: 700; }
.ph-import__toggles :deep(.xl-switch__text span) { font-size: 13px; }

.ph-import__done { padding: 36px 24px; text-align: center; }
.ph-import__done-icon { margin: 0 auto 14px; }
.ph-import__done h2 { margin: 0 0 6px; font-size: 24px; font-weight: var(--xl-font-weight-bold); letter-spacing: -0.03em; }
.ph-import__done-line { margin: 0 0 22px; font-size: 15px; color: var(--xl-color-outline); }
.ph-import__kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; max-width: 520px; margin: 0 auto; }
.ph-import__kpis :deep(.xl-kpi) { text-align: center; }
.ph-import__kpis :deep(.xl-kpi__label) { font-size: 12.5px; }
.ph-import__done-note { margin: 20px 0 0; font-size: 13.5px; color: var(--xl-color-outline); }
.ph-import__failed {
  max-width: 620px;
  margin: 20px auto 0;
  padding: 13px 16px;
  border: 1px solid #F2DCB9;
  border-radius: 14px;
  background: var(--xl-color-warning-container);
  text-align: left;
  font-size: 13px;
  color: var(--xl-color-secondary-text);
}
.ph-import__failed b, .ph-import__failed span { display: block; }
.ph-import__failed span { margin-top: 4px; }
.ph-import__done-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-top: 24px; }
</style>
